package fr.frux.shared.data.photos

import dev.zacsweers.metro.ContributesBinding
import dev.zacsweers.metro.Inject
import dev.zacsweers.metro.SingleIn
import fr.frux.core.common.di.AppScope
import fr.frux.shared.di.IoDispatcher
import fr.frux.shared.domain.photos.PhotoUpload
import fr.frux.shared.domain.photos.PhotoUploadRequest
import fr.frux.shared.domain.photos.PhotoUploadState
import fr.frux.shared.domain.photos.TreePhotoUploads
import fr.frux.shared.domain.repository.TreeRepository
import kotlinx.coroutines.CoroutineDispatcher
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

/** Nombre maximal de photos en échec dont les octets sont gardés, tous arbres confondus. */
const val MAX_RETAINED_FAILED = 10

/**
 * Un unique worker consomme un canal : l'envoi est séquentiel sur toute l'application et l'ordre
 * d'enfilement est celui des appels à [enqueue], quel que soit le dispatcher (un `launch` par photo
 * ne l'aurait garanti que sur un dispatcher mono-thread). Les octets sont relâchés dès que la photo
 * est envoyée. Ceux d'une photo en échec sont gardés pour [retry], dans la limite de
 * [MAX_RETAINED_FAILED] sur toute l'application : au-delà, l'échec le plus ancien est oublié.
 */
@ContributesBinding(AppScope::class)
@SingleIn(AppScope::class)
@Inject
class TreePhotoUploadsImpl(
    private val treeRepository: TreeRepository,
    @IoDispatcher dispatcher: CoroutineDispatcher,
) : TreePhotoUploads {
    private val scope = CoroutineScope(SupervisorJob() + dispatcher)
    private val queue = Channel<Pair<Long, Pending>>(Channel.UNLIMITED)
    private val state = MutableStateFlow<Map<Long, List<PhotoUpload>>>(emptyMap())

    /** Photos en échec, la plus ancienne d'abord. Mise à jour par `update` : worker et appelants s'y croisent. */
    private val failed = MutableStateFlow<List<Triple<Long, String, Pending>>>(emptyList())
    private var worker: Job = startWorker()

    override val uploads: StateFlow<Map<Long, List<PhotoUpload>>> = state

    /** Les octets vivent ici et non dans la coroutine, pour pouvoir les lâcher avant la fin. */
    private class Pending(
        val request: PhotoUploadRequest,
        var bytes: ByteArray?,
    )

    private fun startWorker(): Job =
        scope.launch {
            for ((treeId, pending) in queue) send(treeId, pending)
        }

    override fun enqueue(
        treeId: Long,
        photos: List<PhotoUploadRequest>,
    ) {
        if (photos.isEmpty()) return
        val waiting = photos.map { PhotoUpload(it.id, PhotoUploadState.Waiting) }
        state.update { current -> current + (treeId to (current[treeId].orEmpty() + waiting)) }
        photos.forEach { queue.trySend(treeId to Pending(it, it.bytes)) }
    }

    override fun retry(
        treeId: Long,
        id: String,
    ) {
        val entry = failed.value.firstOrNull { it.first == treeId && it.second == id } ?: return
        if (!dropFailed(treeId, id)) return
        publish(treeId, id, PhotoUploadState.Waiting)
        queue.trySend(treeId to entry.third)
    }

    override fun remove(
        treeId: Long,
        id: String,
    ) {
        if (!dropFailed(treeId, id)) return
        forget(treeId, id)
    }

    override fun forgetSent(treeId: Long) {
        state.update { current ->
            val remaining = current[treeId].orEmpty().filterNot { it.state == PhotoUploadState.Sent }
            if (remaining.isEmpty()) current - treeId else current + (treeId to remaining)
        }
    }

    override fun clear() {
        worker.cancel()
        while (queue.tryReceive().isSuccess) Unit
        failed.value = emptyList()
        state.value = emptyMap()
        worker = startWorker()
    }

    private suspend fun send(
        treeId: Long,
        pending: Pending,
    ) {
        val id = pending.request.id
        val bytes = pending.bytes ?: return
        publish(treeId, id, PhotoUploadState.Sending(null))
        var lastPercent = -1
        val result =
            treeRepository.uploadPhoto(treeId, bytes, pending.request.contentType) { fraction ->
                val percent = (fraction * 100).toInt()
                if (percent != lastPercent) {
                    lastPercent = percent
                    publish(treeId, id, PhotoUploadState.Sending(fraction))
                }
            }
        if (result.isSuccess) {
            pending.bytes = null
            publish(treeId, id, PhotoUploadState.Sent)
        } else {
            retainFailed(treeId, pending)
            publish(treeId, id, PhotoUploadState.Failed)
        }
    }

    private fun retainFailed(
        treeId: Long,
        pending: Pending,
    ) {
        failed.update { it + Triple(treeId, pending.request.id, pending) }
        val excess = failed.value.size - MAX_RETAINED_FAILED
        repeat(excess.coerceAtLeast(0)) {
            val oldest = failed.value.firstOrNull() ?: return
            if (dropFailed(oldest.first, oldest.second)) forget(oldest.first, oldest.second)
        }
    }

    /** Sort la photo des échecs retenus, `false` si un autre appel l'avait déjà fait. */
    private fun dropFailed(
        treeId: Long,
        id: String,
    ): Boolean {
        var found = false
        failed.update { list ->
            found = list.any { it.first == treeId && it.second == id }
            list.filterNot { it.first == treeId && it.second == id }
        }
        return found
    }

    private fun forget(
        treeId: Long,
        id: String,
    ) {
        state.update { current ->
            val remaining = current[treeId].orEmpty().filterNot { it.id == id }
            if (remaining.isEmpty()) current - treeId else current + (treeId to remaining)
        }
    }

    private fun publish(
        treeId: Long,
        id: String,
        newState: PhotoUploadState,
    ) {
        state.update { current ->
            val list = current[treeId] ?: return@update current
            current + (treeId to list.map { if (it.id == id) it.copy(state = newState) else it })
        }
    }
}
